import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {clearSelectedCardPromotion, selectCardPromotionGroup} from '@web/actions/cardPromotion'
import {getDeliveryAddressFee} from '@web/actions/deliveryInfo'
import {isAnyOfSelectPaymentMethodActions, selectFamilyPayPayMethod} from '@web/actions/paymentMethod'
import {
    changeDeliveryAreaExtraFee,
    changeAlwaysUsesAllNaverPoint,
    changeNaverPoint,
    enablePayLaterPointByAllPoint,
    updatePointPayment,
    resetNaverPoint,
    usingAllNaverPoint,
    getAllPointBalance,
    getChargePointBalance,
    getRewardPointBalance,
    toggleUsePointAll,
    changePointAndToggle,
} from '@web/actions/pointPayment'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {ChargePointPolicy, NaverPointStatus, PayMethod} from '@web/core/constants'
import {getInitialState, getNaverPayPointAmount} from '@web/slices/pointPayment/slice'

import type {PointPaymentReducers, MakeSliceOptions} from './interfaces'
import type {PointPaymentState} from '@web/slices/pointPayment/slice'

export default (options?: MakeSliceOptions) =>
    createSlice<PointPaymentState, PointPaymentReducers, 'pointPayment', SliceSelectors<PointPaymentState>>({
        name: 'pointPayment',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(getDeliveryAddressFee.fulfilled, (state, {payload}) => {
                    if (payload.changeDeliveryAreaExtraFeeMessage) {
                        state.naverPayPoint.usingAmount = 0
                    }
                })
                .addCase(toggleUsePointAll, (state, {payload}) => {
                    state.naverPayPoint.usePointAll = payload
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(
                    isAnyOf(
                        changeDeliveryAreaExtraFee.fulfilled,
                        changeNaverPoint.fulfilled,
                        changePointAndToggle.fulfilled,
                        resetNaverPoint.fulfilled,
                        usingAllNaverPoint.fulfilled,
                        changeAlwaysUsesAllNaverPoint.fulfilled,
                        enablePayLaterPointByAllPoint.fulfilled,
                        updatePointPayment.fulfilled,
                    ),
                    (state, {payload}) => {
                        state.naverPayPoint.usingAmount = payload.usingNaverPointAmount
                    },
                )
                .addMatcher(
                    isAnyOf(changeAlwaysUsesAllNaverPoint.fulfilled, changePointAndToggle.fulfilled),
                    (state, {payload}) => {
                        state.naverPayPoint.usePointAll = payload.usePointAll
                    },
                )
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.pointPayment ?? state
                })
                .addMatcher(
                    isAnyOf(
                        getAllPointBalance.fulfilled,
                        getRewardPointBalance.fulfilled,
                        getChargePointBalance.fulfilled,
                    ),
                    (state, {payload}) => {
                        const {rewardPoint, chargePointAmount: chargePoint, isPayMoneyUser, chargePointPolicy} = payload
                        state.naverPayPoint.amount = getNaverPayPointAmount({
                            rewardPoint,
                            chargePoint,
                            isPayMoneyUser: isPayMoneyUser || eq(chargePointPolicy, ChargePointPolicy.PAYMONEY),
                        })
                    },
                )
                .addMatcher(
                    isAnyOf(selectCardPromotionGroup.fulfilled, selectFamilyPayPayMethod.fulfilled),
                    (state) => {
                        state.naverPayPoint.usingAmount = 0
                        if (state.naverPayPoint.status !== NaverPointStatus.NEVER) {
                            state.naverPayPoint.status = NaverPointStatus.DISABLE
                        }
                    },
                )
                .addMatcher(isAnyOf(clearSelectedCardPromotion), (state) => {
                    if (state.naverPayPoint.status !== NaverPointStatus.NEVER) {
                        state.naverPayPoint.status = NaverPointStatus.ENABLE
                    }
                })
                .addMatcher(isAnyOfSelectPaymentMethodActions, (state, {payload}) => {
                    if (
                        state.naverPayPoint.status !== NaverPointStatus.NEVER &&
                        payload.currentPayMethods.includes(PayMethod.FAMILY_PAY) &&
                        !payload.payMethods.includes(PayMethod.FAMILY_PAY)
                    ) {
                        state.naverPayPoint.status = NaverPointStatus.ENABLE
                    }
                })
        },
    })
